import Base from '../../Base/Base';
import { useContext, useEffect, useState } from 'react';
import { Button } from '@upmc/ims.component-library';
import Styles from './CitrixException.module.scss';
import Grid from '@mui/material/Grid';
import CircularProgress from '@mui/material/CircularProgress';
import StyleAuxiliary from '../../../hoc/Auxiliary/StyleAuxiliary/StyleAuxiliary';
import TextField from '@mui/material/TextField';
import Checkbox from '@mui/material/Checkbox';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import MaterialIcon from '@material/react-material-icon';
import { useFormik } from 'formik';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { UserContext } from 'context/User/UserContext';
import Yup from 'utils/validation/yup-extensions';
import FormControlLabel from '@mui/material/FormControlLabel';
import Typography from '@mui/material/Typography';
import { SubmitCitrixException } from './CitrixException_api';

const theme = createTheme({
	palette: {
		primary: {
			main: '#771b61',
		},
	},
});

/**
 * Citrix Exception Form
 *
 * @returns rendered component.
 */

const CitrixExceptionForm = ({
	userSelected,
	setStep,
	setTrackingNumber,
}) => {
	const [formBody, setFormBody] = useState({
		Name: '',
		NTID: '',
		Esignon: '',
		Email: '',
		Department: '',
		Manager: '',
		Requestee_ESOFIID: '',
		Requestor_ESOFIID: '',
		Justification: '',
		Confirmation: false,
	});

	const validationSchema = Yup.object().shape({
		Justification: Yup.string(
			'Enter your justification for access'
		)
			.required('Justification is required.')
			.max(256, 'Maximum length of 256 characters'),
		Confirmation: Yup.boolean()
			.oneOf([true], 'You must accept the terms and conditions.')
			.required('You must accept the terms and conditions.'),
	});

	/**
	 * @type {import('context/User/UserContext').ContextUser}
	 */
	const contextUser = useContext(UserContext);
	const user =
		contextUser.userInfo.impersonationProfile ??
		contextUser.userInfo.userProfile;

	const [submissionSending, setSubmissionSending] = useState(false);
	const [submissionError, setSubmissionError] = useState('');

	useEffect(() => {
		setFormBody({ ...userSelected });
	}, [userSelected]);

	if (!userSelected && !formBody?.NTID && user) {
		setFormBody(prevFormBody => ({
			...prevFormBody,
			Name: user?.Name,
			NTID: user?.NetworkID,
			Esignon: user?.FamiliarESOID,
			Requestee_ESOFIID: user?.ID,
			Requestor_ESOFIID: user?.ID,
			Email: user?.Email,
			Department: user?.Department,
			Manager: user?.Manager,
		}));
	}

	const SubmitException = requiredValues => {
		setSubmissionSending(true);
		setFormBody({
			...formBody,
			Requestor_ESOFIID: user?.ID,
			Justification: requiredValues.Justification,
			Confirmation: requiredValues.Confirmation,
		});

		const submitArgs = {
			...formBody,
			Requestor_ESOFIID: user?.ID,
			Justification: requiredValues.Justification,
			Confirmation: requiredValues.Confirmation,
		};
		SubmitCitrixException(submitArgs).then(resp => {
			const trackingNumber = resp?.data?.payload;
			// Only advance on a successful save; a failed submission returns an
			// error or no tracking number.
			if (
				resp?.error ||
				resp?.data?.responseCode === 500 ||
				!trackingNumber
			) {
				setSubmissionError(
					'Your request could not be submitted. Please try again.'
				);
				setSubmissionSending(false);
				return;
			}
			if (setTrackingNumber) {
				setTrackingNumber(trackingNumber);
			}
			setSubmissionError('');
			setStep();
			setSubmissionSending(false);
		});
	};

	const formik = useFormik({
		initialValues: {
			Justification: '',
			Confirmation: false,
		},
		validationSchema,
		onSubmit: values => {
			SubmitException(values);
		},
	});

	return (
		<Base
			title={`Exception for ${userSelected ? 'Other' : 'Self'}`}
			className={Styles['base--default']}
		>
			<form onSubmit={formik.handleSubmit}>
				<StyleAuxiliary>
					<ThemeProvider theme={theme}>
						<Grid container sx={{ marginBottom: '16px' }}>
							<Grid item xs={12}>
								<p style={{ margin: 0 }}>
									This page allows users to request an exception
									when UPMC MyApps security policies prevent
									certain activities on non-UPMC devices, such
									as downloading files or printing UPMC data.
									The request collects the application,
									restricted activity, and business
									justification for review.
								</p>
							</Grid>
						</Grid>
						<Grid
							container
							direction='row'
							spacing={1}
							sx={{
								alignItems: 'center',
							}}
						>
							<Grid item xs={12} sm={6} md={6} lg={4} xl={4}>
								<TextField
									disabled
									fullWidth
									value={formBody.Name}
									id='outlined-name-input'
									label='Name'
									type='text'
								/>
							</Grid>
							<Grid item xs={12} sm={6} md={6} lg={4} xl={4}>
								<TextField
									disabled
									value={formBody.NTID}
									fullWidth
									id='outlined-networkID-input'
									label='Network ID'
									type='text'
								/>
							</Grid>
							<Grid item xs={12} sm={6} md={6} lg={4} xl={4}>
								<TextField
									disabled
									value={formBody.Esignon}
									fullWidth
									id='outlined-esignon-input'
									label='ESignOn'
									type='text'
								/>
							</Grid>
							<Grid item xs={12} sm={6} md={6} lg={4} xl={4}>
								<TextField
									disabled
									value={formBody.Email}
									fullWidth
									id='outlined-email-input'
									label='Email'
									type='text'
								/>
							</Grid>
							<Grid item xs={12} sm={6} md={6} lg={4} xl={4}>
								<TextField
									disabled
									value={formBody.Department}
									fullWidth
									id='outlined-department-input'
									label='Department'
									type='text'
								/>
							</Grid>
							<Grid item xs={12} sm={6} md={6} lg={4} xl={4}>
								<TextField
									disabled
									value={formBody.Manager}
									fullWidth
									id='outlined-manager-input'
									label='Manager'
									type='text'
								/>
							</Grid>
							<Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
								<TextField
									value={formik.values.Justification}
									onChange={formik.handleChange}
									onBlur={formik.handleBlur}
									name='Justification'
									error={
										(formik.touched.Justification ||
											formik.submitCount > 0 ||
											formik.values.Justification.length >
												256) &&
										Boolean(formik.errors.Justification)
									}
									helperText={
										(formik.touched.Justification ||
											formik.submitCount > 0 ||
											formik.values.Justification.length >
												256) &&
										formik.errors.Justification
									}
									fullWidth
									multiline
									rows={4}
									id='outlined-Justification-input'
									label='Justification of Access'
									type='text'
								/>
							</Grid>
							<Grid
								item
								xs={12}
								sm={12}
								md={12}
								lg={12}
								xl={12}
								alignItems='flex-start'
							>
								<FormControl
									error={
										(formik.touched.Confirmation ||
											formik.submitCount > 0) &&
										Boolean(formik.errors.Confirmation)
									}
								>
									<FormControlLabel
										control={
											<Checkbox
												checked={formik.values.Confirmation}
												onChange={event => {
													formik.handleChange(event);
													formik.setFieldTouched(
														'Confirmation',
														true,
														false
													);
												}}
												name='Confirmation'
												fullWidth
												id='outlined-usage-terms-toggle'
											/>
										}
										label={
											<Typography>
												I consent that for all Remote Worker Exception requests, by myself and on behalf of others, I will not include protected, confidential, or proprietary data such as PHI, PII, account passwords, social security numbers, contracts, UPMC financial data, HR confidential data, homeland security data, or other restricted information.
											</Typography>
										}
									/>
									<FormHelperText>
										{(formik.touched.Confirmation ||
											formik.submitCount > 0) &&
											formik.errors.Confirmation}
									</FormHelperText>
								</FormControl>
							</Grid>
						</Grid>
						<Grid
							container
							direction='column'
							spacing={3}
							sx={{ alignItems: 'center' }}
						>
							<Grid item></Grid>
							{submissionError && (
								<Grid item>
									<p style={{ margin: 0, color: '#d32f2f' }}>
										{submissionError}
									</p>
								</Grid>
							)}
							<Grid item>
								<Button
									type='submit'
									startIcon={
										submissionSending ? (
											<CircularProgress size={24} />
										) : (
											<MaterialIcon icon='save' />
										)
									}
									variant='contained'
									disabled={
										!formik.dirty ||
										!formik.isValid ||
										submissionSending
									}
									sx={{ margin: '5px' }}
									id='SubmitCitrixException'
								>
									{`${submissionSending ? 'Submitting' : 'Submit'}`}
								</Button>
							</Grid>
						</Grid>
					</ThemeProvider>
				</StyleAuxiliary>
			</form>
		</Base>
	);
};

export default CitrixExceptionForm;
